import { useId } from 'react';

import { cx } from '../cx';
import { CORES } from '../tokens';

/**
 * Medalhas do Hall of Fame Grow uP (uP Awards): um circulo dourado com o
 * desenho do premio. Os nomes dos desenhos sao os de `HOF_ICONS`
 * (@growup/site-shared); um desconhecido desenha-se como `medalha`.
 */

/** Desenhos numa caixa de 24 x 24, so com preenchimento (ficam iguais na imagem da carta). */
const GLYPHS: Record<string, string> = {
  medalha:
    'M12 3.5a8.5 8.5 0 1 0 0 17 8.5 8.5 0 0 0 0-17zm0 3.6 1.5 3.1 3.4.5-2.5 2.4.6 3.4L12 14.9l-3 1.6.6-3.4-2.5-2.4 3.4-.5L12 7.1z',
  trofeu:
    'M7 3h10v2h3v3c0 2.8-2 5-4.6 5.4-.8 1.6-2 2.6-3.4 2.8V19h3v2H9v-2h3v-2.8c-1.4-.2-2.6-1.2-3.4-2.8C6 13 4 10.8 4 8V5h3V3zM6 7v1c0 1.5.8 2.8 2 3.4-.3-.9-.5-1.9-.5-2.9V7H6zm12 0h-1.5v1.5c0 1-.2 2-.5 2.9 1.2-.6 2-1.9 2-3.4V7z',
  estrela: 'M12 2l2.9 6.6 7.1.7-5.4 4.7 1.6 7L12 17.3 5.8 21l1.6-7L2 9.3l7.1-.7L12 2z',
  coracao:
    'M12 21s-9-6-9-12.2C3 5.6 5.4 3.5 8 3.5c1.8 0 3.2 1 4 2.3.8-1.3 2.2-2.3 4-2.3 2.6 0 5 2.1 5 5.3C21 15 12 21 12 21z',
  comando:
    'M7 7h10a5 5 0 0 1 4.8 6.4l-1.2 4.1a2.5 2.5 0 0 1-4.3 1L14.5 16h-5l-1.8 2.5a2.5 2.5 0 0 1-4.3-1l-1.2-4.1A5 5 0 0 1 7 7zm0 2.5v1.5H5.5v1.5H7V14h1.5v-1.5H10V11H8.5V9.5H7zm9 0a1 1 0 1 0 0 2 1 1 0 0 0 0-2zm-1.5 2.5a1 1 0 1 0 0 2 1 1 0 0 0 0-2z',
  microfone:
    'M12 2a3.5 3.5 0 0 1 3.5 3.5v6a3.5 3.5 0 0 1-7 0v-6A3.5 3.5 0 0 1 12 2zM6 11h1.6a4.4 4.4 0 0 0 8.8 0H18a6 6 0 0 1-5.2 5.9V20H16v2H8v-2h3.2v-3.1A6 6 0 0 1 6 11z',
  camara:
    'M4 7h3l1.5-2.5h7L17 7h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1zm8 3a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7z',
  escudo: 'M12 2l8 3v6c0 5-3.4 9.4-8 11-4.6-1.6-8-6-8-11V5l8-3z',
  raio: 'M13 2L4 14h7l-1 8 10-13h-7l0-7z',
  coroa: 'M3 7l4.5 4L12 4l4.5 7L21 7l-2 12H5L3 7z',
};

function glyph(icon: string): string {
  return GLYPHS[icon] ?? (GLYPHS.medalha as string);
}

/** As cores da medalha: ouro, como a faixa Gold da carta. */
const GOLD_LIGHT = CORES['tier-gold-from'];
const GOLD_DARK = CORES['tier-gold-to'];
const GLYPH_INK = CORES['brand-charcoal'];

export type HofMedalGlyphProps = {
  icon: string;
  /** Centro e raio, nas coordenadas do SVG onde a medalha fica. */
  cx: number;
  cy: number;
  r: number;
  gradientId: string;
  title?: string;
};

/** A medalha como `<g>`, para dentro de outro SVG (a carta de membro). */
export function HofMedalGlyph({ icon, cx: x, cy: y, r, gradientId, title }: HofMedalGlyphProps) {
  const scale = (r * 1.15) / 24;
  return (
    <g data-medal={icon}>
      {title ? <title>{title}</title> : null}
      <circle cx={x} cy={y} r={r} fill={`url(#${gradientId})`} />
      <circle
        cx={x}
        cy={y}
        r={r - 0.75}
        fill="none"
        stroke="#000"
        strokeOpacity="0.25"
        strokeWidth="1.5"
      />
      <path
        d={glyph(icon)}
        fill={GLYPH_INK}
        fillRule="evenodd"
        transform={`translate(${String(x - 12 * scale)} ${String(y - 12 * scale)}) scale(${String(scale)})`}
      />
    </g>
  );
}

/** O gradiente dourado das medalhas, para pôr no `<defs>` de um SVG. */
export function HofMedalGradient({ id }: { id: string }) {
  return (
    <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stopColor={GOLD_LIGHT} />
      <stop offset="1" stopColor={GOLD_DARK} />
    </linearGradient>
  );
}

export type HofMedalProps = {
  icon: string;
  /** Lado, em pixeis. */
  size?: number;
  /** Texto para quem nao ve a imagem (e dica ao passar o rato). Sem ele, e decorativa. */
  title?: string;
  className?: string;
};

/** Uma medalha sozinha, para listas e para a pagina do Hall of Fame. */
export function HofMedal({ icon, size = 32, title, className }: HofMedalProps) {
  const id = `${useId().replace(/:/g, '')}-gold`;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 32 32"
      width={size}
      height={size}
      className={cx('shrink-0', className)}
      {...(title ? { role: 'img', 'aria-label': title } : { 'aria-hidden': true })}
    >
      <defs>
        <HofMedalGradient id={id} />
      </defs>
      <HofMedalGlyph icon={icon} cx={16} cy={16} r={15} gradientId={id} />
    </svg>
  );
}
